import React, { useState, useEffect } from "react";
import {
  X,
  User,
  Envelope,
  Calendar,
  Shield,
  Target,
  PiggyBank,
  TrendUp,
  SignOut,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";

interface UserProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSignOut?: () => void;
}

interface UserProfile {
  name: string;
  email?: string; // Email is optional, can be added later
  age?: number;
  riskTolerance: "conservative" | "moderate" | "aggressive";
  investmentGoals: string[];
  emergencyFundTarget: number;
  retirementAge: number;
  targetSavingsRate: number;
  preferredInvestmentTypes: string[];
}

const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({
  isOpen,
  onClose,
  onSignOut,
}) => {
  const { state, dispatch, getCurrentUser } = useData();
  const currentUser = getCurrentUser();

  const [profile, setProfile] = useState<UserProfile>({
    name: currentUser?.name || "",
    email: currentUser?.email || "",
    age: currentUser?.age,
    riskTolerance: currentUser?.riskTolerance || "moderate",
    investmentGoals: currentUser?.investmentGoals || [
      "retirement",
      "emergency-fund",
    ],
    emergencyFundTarget: currentUser?.emergencyFundTarget || 15000,
    retirementAge: currentUser?.retirementAge || 65,
    targetSavingsRate: currentUser?.targetSavingsRate || 20,
    preferredInvestmentTypes: currentUser?.preferredInvestmentTypes || [
      "index-funds",
      "etfs",
    ],
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSignOut = () => {
    // Clear local storage or any local auth state
    if (onSignOut) {
      onSignOut();
    }
    onClose();
  };

  useEffect(() => {
    if (currentUser && !isEditing) {
      setProfile({
        name: currentUser.name || "",
        email: currentUser.email || "",
        age: currentUser.age,
        riskTolerance: currentUser.riskTolerance || "moderate",
        investmentGoals: currentUser.investmentGoals || [
          "retirement",
          "emergency-fund",
        ],
        emergencyFundTarget: currentUser.emergencyFundTarget || 15000,
        retirementAge: currentUser.retirementAge || 65,
        targetSavingsRate: currentUser.targetSavingsRate || 20,
        preferredInvestmentTypes: currentUser.preferredInvestmentTypes || [
          "index-funds",
          "etfs",
        ],
      });
    }
  }, [currentUser?.id, isEditing]);

  const handleSave = async () => {
    if (!currentUser) {
      alert("User not found. Please try signing in again.");
      return;
    }

    // Validate required fields
    if (!profile.name.trim()) {
      alert("Name is required.");
      return;
    }

    setIsSaving(true);
    try {
      // Update local state directly (no backend needed)
      const updatedUser = {
        ...currentUser,
        name: profile.name.trim(),
        email: profile.email?.trim() || undefined,
        age: profile.age,
        riskTolerance: profile.riskTolerance,
        investmentGoals: profile.investmentGoals,
        emergencyFundTarget: profile.emergencyFundTarget,
        retirementAge: profile.retirementAge,
        targetSavingsRate: profile.targetSavingsRate,
        preferredInvestmentTypes: profile.preferredInvestmentTypes,
      };

      // Update local state to reflect changes
      dispatch({
        type: "UPDATE_USER",
        payload: updatedUser,
      });

      setIsEditing(false);
    } catch (error: any) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDollarInputChange = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    return numericValue === "" ? 0 : parseInt(numericValue);
  };

  const formatDollarValue = (value: number): string => {
    if (value === 0) return "";
    return value.toLocaleString();
  };

  const riskToleranceOptions = [
    {
      value: "conservative",
      label: "Conservative",
      description: "Low risk, stable returns",
    },
    {
      value: "moderate",
      label: "Moderate",
      description: "Balanced risk and return",
    },
    {
      value: "aggressive",
      label: "Aggressive",
      description: "Higher risk, higher potential returns",
    },
  ];

  const investmentGoalOptions = [
    { value: "retirement", label: "Retirement", icon: Target },
    { value: "emergency-fund", label: "Emergency Fund", icon: Shield },
    {
      value: "house-down-payment",
      label: "House Down Payment",
      icon: PiggyBank,
    },
    { value: "education", label: "Education", icon: TrendUp },
    { value: "vacation", label: "Vacation", icon: Target },
    { value: "business", label: "Business Investment", icon: TrendUp },
  ];

  const investmentTypeOptions = [
    { value: "index-funds", label: "Index Funds" },
    { value: "etfs", label: "ETFs" },
    { value: "individual-stocks", label: "Individual Stocks" },
    { value: "bonds", label: "Bonds" },
    { value: "real-estate", label: "Real Estate" },
    { value: "crypto", label: "Cryptocurrency" },
  ];

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:w-96 bg-stone-50 dark:bg-gray-800 transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 border-b border-gray-200 dark:border-gray-700 space-y-3 sm:space-y-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Profile Settings
            </h2>
            <button
              onClick={onClose}
              className="btn-icon self-end sm:self-auto"
              aria-label="Close profile drawer">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                <User className="w-5 h-5 mr-2" />
                Basic Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) =>
                      setProfile({ ...profile, name: e.target.value })
                    }
                    className={`input w-full ${
                      isEditing
                        ? "border-primary-300 focus:border-primary-500"
                        : "bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    }`}
                    placeholder="Enter your name"
                    disabled={!isEditing}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={profile.email || ""}
                      onChange={(e) =>
                        setProfile({ ...profile, email: e.target.value })
                      }
                      className={`input w-full ${
                        isEditing
                          ? "border-primary-300 focus:border-primary-500"
                          : "bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      }`}
                      placeholder="Enter your email"
                      disabled={!isEditing}
                    />
                  </div>
                  {!profile.email && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Add an email to receive notifications and secure your
                      account
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Age
                  </label>
                  <input
                    type="number"
                    value={profile.age || ""}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        age: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      })
                    }
                    className={`input w-full ${
                      isEditing
                        ? "border-primary-300 focus:border-primary-500"
                        : "bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    }`}
                    placeholder="Enter your age"
                    min="18"
                    max="120"
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>

            {/* Investment Preferences */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                <TrendUp className="w-5 h-5 mr-2" />
                Investment Preferences
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Risk Tolerance
                </label>
                <div className="space-y-2">
                  {riskToleranceOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        profile.riskTolerance === option.value
                          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                          : "border-gray-200 dark:border-gray-600 hover:border-primary-300"
                      }`}>
                      <input
                        type="radio"
                        name="riskTolerance"
                        value={option.value}
                        checked={profile.riskTolerance === option.value}
                        onChange={(e) =>
                          setProfile({
                            ...profile,
                            riskTolerance: e.target.value as any,
                          })
                        }
                        className="sr-only"
                        disabled={!isEditing}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {option.label}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {option.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Investment Goals
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {investmentGoalOptions.map((goal) => (
                    <label
                      key={goal.value}
                      className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        profile.investmentGoals.includes(goal.value)
                          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                          : "border-gray-200 dark:border-gray-600 hover:border-primary-300"
                      }`}>
                      <input
                        type="checkbox"
                        checked={profile.investmentGoals.includes(goal.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setProfile({
                              ...profile,
                              investmentGoals: [
                                ...profile.investmentGoals,
                                goal.value,
                              ],
                            });
                          } else {
                            setProfile({
                              ...profile,
                              investmentGoals: profile.investmentGoals.filter(
                                (g) => g !== goal.value
                              ),
                            });
                          }
                        }}
                        className="sr-only"
                        disabled={!isEditing}
                      />
                      <goal.icon className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {goal.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Preferred Investment Types
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {investmentTypeOptions.map((type) => (
                    <label
                      key={type.value}
                      className={`flex items-center p-2 rounded-lg border-2 cursor-pointer transition-all ${
                        profile.preferredInvestmentTypes.includes(type.value)
                          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                          : "border-gray-200 dark:border-gray-600 hover:border-primary-300"
                      }`}>
                      <input
                        type="checkbox"
                        checked={profile.preferredInvestmentTypes.includes(
                          type.value
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setProfile({
                              ...profile,
                              preferredInvestmentTypes: [
                                ...profile.preferredInvestmentTypes,
                                type.value,
                              ],
                            });
                          } else {
                            setProfile({
                              ...profile,
                              preferredInvestmentTypes:
                                profile.preferredInvestmentTypes.filter(
                                  (t) => t !== type.value
                                ),
                            });
                          }
                        }}
                        className="sr-only"
                        disabled={!isEditing}
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {type.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Financial Targets */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                <Target className="w-5 h-5 mr-2" />
                Financial Targets
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Emergency Fund Target
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600">
                      $
                    </span>
                    <input
                      type="text"
                      value={formatDollarValue(profile.emergencyFundTarget)}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          emergencyFundTarget: handleDollarInputChange(
                            e.target.value
                          ),
                        })
                      }
                      className="input w-full pl-8"
                      placeholder="0"
                      disabled={!isEditing}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Target Retirement Age
                  </label>
                  <input
                    type="number"
                    value={profile.retirementAge}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        retirementAge: parseInt(e.target.value) || 65,
                      })
                    }
                    className="input w-full"
                    min="40"
                    max="80"
                    disabled={!isEditing}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Target Savings Rate (%)
                  </label>
                  <input
                    type="number"
                    value={profile.targetSavingsRate}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        targetSavingsRate: parseInt(e.target.value) || 20,
                      })
                    }
                    className="input w-full"
                    min="5"
                    max="80"
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex space-x-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSaving ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </div>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    disabled={isSaving}
                    className="btn-secondary flex-1 disabled:opacity-50">
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn-primary flex-1">
                    Edit Profile
                  </button>
                  <button onClick={onClose} className="btn-secondary flex-1">
                    Close
                  </button>
                </>
              )}
            </div>

            {/* Sign Out Button */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center justify-center space-x-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-4 py-2 rounded-lg transition-colors">
                <SignOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfileDrawer;
